/**
 * Asks the server to send a form's confirmation email for the response this client just
 * saved. Never throws: failures are logged (prefixed with `source`) rather than surfaced,
 * since a confirmation-email failure shouldn't block a successful submission.
 *
 * Shared by every form submit surface (standalone form page, landing-page modals, checkout
 * modal) so the request shape and failure handling can't drift between them. The server
 * decides the recipient, template and personalization from the stored response; the client
 * only proves it just saved that response with the `confirmToken` save-response returned.
 *
 * @param params.formId - The form being confirmed.
 * @param params.responseId - The response this submission just saved.
 * @param params.confirmToken - The short-lived confirmation token save-response returned with it.
 * @param params.source - Short label identifying the caller, used to prefix log lines.
 * @returns A promise that resolves once the send attempt (success or failure) completes.
 */
export async function sendFormConfirmationEmail(params: {
  formId: number;
  responseId: number | null | undefined;
  confirmToken: string | null | undefined;
  source: string;
}): Promise<void> {
  const { formId, responseId, confirmToken, source } = params;

  if (responseId == null || !confirmToken) {
    // No token means save-response could not mint one (EDIT_TOKEN_SECRET unset), so the server
    // would refuse the send anyway.
    console.error(`${source}: no confirmation token for this response, skipping the confirmation email`);
    return;
  }

  try {
    const response = await fetch("/api/forms/send-bulk-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        formId,
        // Declares this as a confirmation so the route treats it as one even when the submitter
        // manages the form (e.g. the owner testing their own form).
        kind: "confirmation",
        responseId,
        confirmToken,
      }),
    });

    if (!response.ok) {
      console.error(`${source}: confirmation email request failed`, response.status);
      return;
    }

    const result = await response.json().catch(() => null);
    if (result?.failureCount > 0) {
      console.error(`${source}: confirmation email failed to send`, result);
    }
  } catch (emailError) {
    console.error(`${source}: error sending confirmation email`, emailError);
  }
}
